import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { WizardShell } from '../components/WizardShell';
import { TextField } from '../components/form/TextField';
import { PhoneField } from '../components/form/PhoneField';
import { SelectField } from '../components/form/SelectField';
import { RadioGroup } from '../components/form/RadioGroup';
import { CheckboxField } from '../components/form/CheckboxField';
import { YesNoToggle } from '../components/form/YesNoToggle';
import { CurrencyField } from '../components/form/CurrencyField';
import { PercentField } from '../components/form/PercentField';
import { DateField } from '../components/form/DateField';
import { SectionCard } from '../components/form/SectionCard';
import { FieldGrid } from '../components/form/FieldGrid';
import { ApplicationProvider, useApplicationState, calculateCompletion } from '../state/ApplicationContext';
import { CrmHeader } from '../components/application/sections/CrmHeader';
import { ClientGoalsSection } from '../components/application/sections/ClientGoalsSection';
import { BorrowerManagementSection } from '../components/application/sections/BorrowerManagementSection';
const STEPS = [{
  id: 'goals',
  label: 'Client Goals'
}, {
  id: 'borrowers_dynamic',
  label: 'Borrowers & Title'
}, {
  id: 'employment',
  label: 'Employment & Income'
}, {
  id: 'property',
  label: 'Subject Property'
}, {
  id: 'purchase',
  label: 'Purchase Details'
}, {
  id: 'housing',
  label: 'Housing Expenses'
}, {
  id: 'assets',
  label: 'Assets & Credit'
}, {
  id: 'review',
  label: 'Review & Submit'
}];
const US_STATES = [{
  value: 'AL',
  label: 'Alabama'
}, {
  value: 'AK',
  label: 'Alaska'
}, {
  value: 'AZ',
  label: 'Arizona'
}, {
  value: 'AR',
  label: 'Arkansas'
}, {
  value: 'CA',
  label: 'California'
}, {
  value: 'CO',
  label: 'Colorado'
}, {
  value: 'CT',
  label: 'Connecticut'
}, {
  value: 'DE',
  label: 'Delaware'
}, {
  value: 'FL',
  label: 'Florida'
}, {
  value: 'GA',
  label: 'Georgia'
}, {
  value: 'HI',
  label: 'Hawaii'
}, {
  value: 'ID',
  label: 'Idaho'
}, {
  value: 'IL',
  label: 'Illinois'
}, {
  value: 'IN',
  label: 'Indiana'
}, {
  value: 'IA',
  label: 'Iowa'
}, {
  value: 'KS',
  label: 'Kansas'
}, {
  value: 'KY',
  label: 'Kentucky'
}, {
  value: 'LA',
  label: 'Louisiana'
}, {
  value: 'ME',
  label: 'Maine'
}, {
  value: 'MD',
  label: 'Maryland'
}, {
  value: 'MA',
  label: 'Massachusetts'
}, {
  value: 'MI',
  label: 'Michigan'
}, {
  value: 'MN',
  label: 'Minnesota'
}, {
  value: 'MS',
  label: 'Mississippi'
}, {
  value: 'MO',
  label: 'Missouri'
}, {
  value: 'MT',
  label: 'Montana'
}, {
  value: 'NE',
  label: 'Nebraska'
}, {
  value: 'NV',
  label: 'Nevada'
}, {
  value: 'NH',
  label: 'New Hampshire'
}, {
  value: 'NJ',
  label: 'New Jersey'
}, {
  value: 'NM',
  label: 'New Mexico'
}, {
  value: 'NY',
  label: 'New York'
}, {
  value: 'NC',
  label: 'North Carolina'
}, {
  value: 'ND',
  label: 'North Dakota'
}, {
  value: 'OH',
  label: 'Ohio'
}, {
  value: 'OK',
  label: 'Oklahoma'
}, {
  value: 'OR',
  label: 'Oregon'
}, {
  value: 'PA',
  label: 'Pennsylvania'
}, {
  value: 'RI',
  label: 'Rhode Island'
}, {
  value: 'SC',
  label: 'South Carolina'
}, {
  value: 'SD',
  label: 'South Dakota'
}, {
  value: 'TN',
  label: 'Tennessee'
}, {
  value: 'TX',
  label: 'Texas'
}, {
  value: 'UT',
  label: 'Utah'
}, {
  value: 'VT',
  label: 'Vermont'
}, {
  value: 'VA',
  label: 'Virginia'
}, {
  value: 'WA',
  label: 'Washington'
}, {
  value: 'WV',
  label: 'West Virginia'
}, {
  value: 'WI',
  label: 'Wisconsin'
}, {
  value: 'WY',
  label: 'Wyoming'
}];
function PurchaseApplicationContent() {
  const navigate = useNavigate();
  const screenInit = useScreenInit();
  const {
    state: appState
  } = useApplicationState();
  const completionPercentage = calculateCompletion(appState);
  const [currentStep, setCurrentStep] = useState(screenInit.currentStep ?? 0);
  const [submitted, setSubmitted] = useState(false);
  // Borrower Info
  const [isVeteran, setIsVeteran] = useState<boolean | null>(null);
  const [disabilityPercent, setDisabilityPercent] = useState('');
  const [email, setEmail] = useState('');
  const [borrowerName, setBorrowerName] = useState('');
  const [isMarried, setIsMarried] = useState<boolean | null>(null);
  const [ssn, setSsn] = useState('');
  const [dob, setDob] = useState('');
  const [creditScore, setCreditScore] = useState('');
  const [homePhone, setHomePhone] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [workPhone, setWorkPhone] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [county, setCounty] = useState('');
  // Employment
  const [employerName, setEmployerName] = useState('');
  const [position, setPosition] = useState('');
  const [yearsEmployed, setYearsEmployed] = useState('');
  const [income, setIncome] = useState('');
  const [employmentType, setEmploymentType] = useState('');
  const [employerAddress, setEmployerAddress] = useState('');
  const [employerPhone, setEmployerPhone] = useState('');
  const [prevEmployerName, setPrevEmployerName] = useState('');
  const [prevPosition, setPrevPosition] = useState('');
  const [prevYearsEmployed, setPrevYearsEmployed] = useState('');
  const [prevIncome, setPrevIncome] = useState('');
  const [prevEmploymentType, setPrevEmploymentType] = useState('');
  const [ssi, setSsi] = useState('');
  const [pension, setPension] = useState('');
  const [vaDisability, setVaDisability] = useState('');
  const [additionalIncome, setAdditionalIncome] = useState('');
  const [nextPayRaise, setNextPayRaise] = useState('');
  const [creditCards, setCreditCards] = useState('');
  const [autoLoans, setAutoLoans] = useState('');
  const [personalLoans, setPersonalLoans] = useState('');
  const [otherDebt, setOtherDebt] = useState('');
  // Co-Borrower
  const [noCoBorrower, setNoCoBorrower] = useState(false);
  const [coBorrowerName, setCoBorrowerName] = useState('');
  const [coBorrowerMarried, setCoBorrowerMarried] = useState<boolean | null>(null);
  const [coBorrowerSsn, setCoBorrowerSsn] = useState('');
  const [coBorrowerDob, setCoBorrowerDob] = useState('');
  const [coBorrowerCreditScore, setCoBorrowerCreditScore] = useState('');
  const [coBorrowerEmployer, setCoBorrowerEmployer] = useState('');
  const [coBorrowerPosition, setCoBorrowerPosition] = useState('');
  const [coBorrowerYears, setCoBorrowerYears] = useState('');
  const [coBorrowerIncome, setCoBorrowerIncome] = useState('');
  const [coBorrowerEmploymentType, setCoBorrowerEmploymentType] = useState('');
  const [coBorrowerPrevEmployer, setCoBorrowerPrevEmployer] = useState('');
  const [coBorrowerPrevPosition, setCoBorrowerPrevPosition] = useState('');
  const [coBorrowerPrevYears, setCoBorrowerPrevYears] = useState('');
  const [coBorrowerPrevIncome, setCoBorrowerPrevIncome] = useState('');
  const [coBorrowerSsi, setCoBorrowerSsi] = useState('');
  const [coBorrowerPension, setCoBorrowerPension] = useState('');
  const [coBorrowerVaDisability, setCoBorrowerVaDisability] = useState('');
  const [coBorrowerAdditionalIncome, setCoBorrowerAdditionalIncome] = useState('');
  const [coBorrowerNextPayRaise, setCoBorrowerNextPayRaise] = useState('');
  // Property
  const [occupancy, setOccupancy] = useState('');
  const [propertyType, setPropertyType] = useState('');
  const [fhaApproved, setFhaApproved] = useState<boolean | null>(null);
  const [vaApproved, setVaApproved] = useState<boolean | null>(null);
  const [propertyNotSelected, setPropertyNotSelected] = useState(false);
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertyCity, setPropertyCity] = useState('');
  const [propertyState, setPropertyState] = useState('');
  const [propertyZip, setPropertyZip] = useState('');
  const [desiredZip, setDesiredZip] = useState('');
  // Purchase
  const [purchasePrice, setPurchasePrice] = useState('');
  const [downPaymentPercent, setDownPaymentPercent] = useState('');
  const [downPaymentDollar, setDownPaymentDollar] = useState('');
  const [loanProduct, setLoanProduct] = useState('');
  const [contractSigned, setContractSigned] = useState<boolean | null>(null);
  const [closingDate, setClosingDate] = useState('');
  const [hasRealtor, setHasRealtor] = useState<boolean | null>(null);
  const [realtorName, setRealtorName] = useState('');
  const [realtorPhone, setRealtorPhone] = useState('');
  const [earnestMoneyMade, setEarnestMoneyMade] = useState<boolean | null>(null);
  const [earnestMoneyAmount, setEarnestMoneyAmount] = useState('');
  // Housing
  const [escrowed, setEscrowed] = useState<boolean | null>(null);
  const [propertyTaxes, setPropertyTaxes] = useState('');
  const [homeownersInsurance, setHomeownersInsurance] = useState('');
  const [pmi, setPmi] = useState('');
  const [floodInsurance, setFloodInsurance] = useState('');
  const [hoa, setHoa] = useState('');
  // Assets & Credit
  const [assetType, setAssetType] = useState('');
  const [assetValue, setAssetValue] = useState('');
  const [foreclosure, setForeclosure] = useState<boolean | null>(null);
  const [bankruptcy, setBankruptcy] = useState<boolean | null>(null);
  const [bankruptcyChapter, setBankruptcyChapter] = useState('');
  const [liens, setLiens] = useState<boolean | null>(null);
  const [lateMortgage, setLateMortgage] = useState<boolean | null>(null);
  const [farmActivity, setFarmActivity] = useState<boolean | null>(null);
  const [currentMortgage, setCurrentMortgage] = useState<boolean | null>(null);
  const [childSupport, setChildSupport] = useState<boolean | null>(null);
  const [loanModification, setLoanModification] = useState<boolean | null>(null);
  const [deferredInterest, setDeferredInterest] = useState<boolean | null>(null);
  const [solarPanels, setSolarPanels] = useState<boolean | null>(null);
  const [forbearance, setForbearance] = useState<boolean | null>(null);
  const [reinstated, setReinstated] = useState<boolean | null>(null);
  const [amountOwed, setAmountOwed] = useState('');
  const [dateOwed, setDateOwed] = useState('');
  const [sellingProperty, setSellingProperty] = useState<boolean | null>(null);
  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      const nextIndex = currentStep + 1;
      setCurrentStep(nextIndex);
      document.getElementById(STEPS[nextIndex].id)?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      const prevIndex = currentStep - 1;
      setCurrentStep(prevIndex);
      document.getElementById(STEPS[prevIndex].id)?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleSubmit = () => {
    setSubmitted(true);
  };
  if (submitted) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-249">
        <div className="max-w-2xl w-full bg-white rounded-2xl p-8 text-center shadow-lg" data-id="element-250">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-251">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-252">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-253" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-254">
            Application Submitted!
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-255">
            Your purchase mortgage application has been successfully submitted.
            Our team will review your information and contact you within 24-48
            hours.
          </p>
          <button onClick={() => navigate('/')} className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700" data-id="element-256">
            Return to Home
          </button>
        </div>
      </div>;
  }
  const getStepContent = (index: number) => {
    const stepId = STEPS[index].id;
    if (stepId === 'goals') return <ClientGoalsSection data-id="element-257" />;
    if (stepId === 'borrowers_dynamic') return <BorrowerManagementSection data-id="element-258" />;
    const legacyCaseById: Record<string, number> = {
      employment: 1,
      property: 3,
      purchase: 4,
      housing: 5,
      assets: 6,
      review: 7
    };
    switch (legacyCaseById[stepId]) {
      case 0:
        // Borrower Information (legacy — no longer reachable)
        return <div className="space-y-6" data-id="element-259">
            <SectionCard title="Personal Details" data-id="element-260">
              <div className="space-y-4" data-id="element-261">
                <FieldGrid data-id="element-262">
                  <YesNoToggle label="Veteran" value={isVeteran} onChange={setIsVeteran} data-id="element-263" />
                  <PercentField label="Disability Percentage" value={disabilityPercent} onChange={setDisabilityPercent} data-id="element-264" />
                </FieldGrid>
                <TextField label="Email Address" type="email" value={email} onChange={setEmail} required data-id="element-265" />
                <FieldGrid data-id="element-266">
                  <TextField label="Borrower Name" value={borrowerName} onChange={setBorrowerName} required data-id="element-267" />
                  <YesNoToggle label="Married" value={isMarried} onChange={setIsMarried} data-id="element-268" />
                </FieldGrid>
                <FieldGrid data-id="element-269">
                  <TextField label="SSN" value={ssn} onChange={setSsn} placeholder="XXX-XX-XXXX" required data-id="element-270" />
                  <DateField label="Date of Birth" value={dob} onChange={setDob} required data-id="element-271" />
                </FieldGrid>
                <TextField label="Credit Score" value={creditScore} onChange={setCreditScore} data-id="element-272" />
              </div>
            </SectionCard>

            <SectionCard title="Contact Information" data-id="element-273">
              <FieldGrid data-id="element-274">
                <PhoneField label="Home Phone" value={homePhone} onChange={setHomePhone} data-id="element-275" />
                <PhoneField label="Cell Phone" value={cellPhone} onChange={setCellPhone} required data-id="element-276" />
                <PhoneField label="Work Phone" value={workPhone} onChange={setWorkPhone} data-id="element-277" />
              </FieldGrid>
            </SectionCard>

            <SectionCard title="Current Address" data-id="element-278">
              <div className="space-y-4" data-id="element-279">
                <TextField label="Street Address" value={street} onChange={setStreet} required data-id="element-280" />
                <FieldGrid data-id="element-281">
                  <TextField label="City" value={city} onChange={setCity} required data-id="element-282" />
                  <SelectField label="State" value={state} onChange={setState} options={US_STATES} required data-id="element-283" />
                </FieldGrid>
                <FieldGrid data-id="element-284">
                  <TextField label="Zip Code" value={zip} onChange={setZip} required data-id="element-285" />
                  <TextField label="County" value={county} onChange={setCounty} data-id="element-286" />
                </FieldGrid>
              </div>
            </SectionCard>
          </div>;
      case 1:
        // Employment & Income
        return <div className="space-y-6" data-id="element-287">
            <SectionCard title="Current Employment" data-id="element-288">
              <div className="space-y-4" data-id="element-289">
                <FieldGrid data-id="element-290">
                  <TextField label="Employer Name" value={employerName} onChange={setEmployerName} required data-id="element-291" />
                  <TextField label="Position / Title" value={position} onChange={setPosition} required data-id="element-292" />
                </FieldGrid>
                <FieldGrid data-id="element-293">
                  <TextField label="Years Employed" value={yearsEmployed} onChange={setYearsEmployed} required data-id="element-294" />
                  <CurrencyField label="Annual Income" value={income} onChange={setIncome} required data-id="element-295" />
                </FieldGrid>
                <RadioGroup label="Employment Type" value={employmentType} onChange={setEmploymentType} options={[{
                value: '1099',
                label: '1099'
              }, {
                value: 'W2',
                label: 'W2'
              }, {
                value: 'Self Employed',
                label: 'Self Employed'
              }]} layout="horizontal" required data-id="element-296" />
                <TextField label="Employer Address" value={employerAddress} onChange={setEmployerAddress} data-id="element-297" />
                <PhoneField label="Employer Phone" value={employerPhone} onChange={setEmployerPhone} data-id="element-298" />
              </div>
            </SectionCard>

            <SectionCard title="Previous Employment" data-id="element-299">
              <div className="space-y-4" data-id="element-300">
                <FieldGrid data-id="element-301">
                  <TextField label="Employer Name" value={prevEmployerName} onChange={setPrevEmployerName} data-id="element-302" />
                  <TextField label="Position / Title" value={prevPosition} onChange={setPrevPosition} data-id="element-303" />
                </FieldGrid>
                <FieldGrid data-id="element-304">
                  <TextField label="Years Employed" value={prevYearsEmployed} onChange={setPrevYearsEmployed} data-id="element-305" />
                  <CurrencyField label="Annual Income" value={prevIncome} onChange={setPrevIncome} data-id="element-306" />
                </FieldGrid>
                <RadioGroup label="Employment Type" value={prevEmploymentType} onChange={setPrevEmploymentType} options={[{
                value: '1099',
                label: '1099'
              }, {
                value: 'W2',
                label: 'W2'
              }, {
                value: 'Self Employed',
                label: 'Self Employed'
              }]} layout="horizontal" data-id="element-307" />
              </div>
            </SectionCard>

            <SectionCard title="Additional Income" data-id="element-308">
              <FieldGrid data-id="element-309">
                <CurrencyField label="SSI" value={ssi} onChange={setSsi} data-id="element-310" />
                <CurrencyField label="Pension" value={pension} onChange={setPension} data-id="element-311" />
                <CurrencyField label="VA Disability" value={vaDisability} onChange={setVaDisability} data-id="element-312" />
                <CurrencyField label="Additional Income" value={additionalIncome} onChange={setAdditionalIncome} data-id="element-313" />
              </FieldGrid>
              <div className="mt-4" data-id="element-314">
                <TextField label="Next Pay Raise" value={nextPayRaise} onChange={setNextPayRaise} data-id="element-315" />
              </div>
            </SectionCard>

            <SectionCard title="Liabilities" data-id="element-316">
              <FieldGrid data-id="element-317">
                <CurrencyField label="Credit Cards (Monthly)" value={creditCards} onChange={setCreditCards} data-id="element-318" />
                <CurrencyField label="Auto Loans (Monthly)" value={autoLoans} onChange={setAutoLoans} data-id="element-319" />
                <CurrencyField label="Personal Loans (Monthly)" value={personalLoans} onChange={setPersonalLoans} data-id="element-320" />
                <CurrencyField label="Other Monthly Debt" value={otherDebt} onChange={setOtherDebt} data-id="element-321" />
              </FieldGrid>
            </SectionCard>
          </div>;
      case 2:
        // Co-Borrower
        return <div className="space-y-6" data-id="element-322">
            <SectionCard data-id="element-323">
              <CheckboxField label="No Co-Borrower" checked={noCoBorrower} onChange={setNoCoBorrower} data-id="element-324" />
            </SectionCard>

            {!noCoBorrower && <>
                <SectionCard title="Personal Details" data-id="element-325">
                  <div className="space-y-4" data-id="element-326">
                    <FieldGrid data-id="element-327">
                      <TextField label="Name" value={coBorrowerName} onChange={setCoBorrowerName} data-id="element-328" />
                      <YesNoToggle label="Married" value={coBorrowerMarried} onChange={setCoBorrowerMarried} data-id="element-329" />
                    </FieldGrid>
                    <FieldGrid data-id="element-330">
                      <TextField label="SSN" value={coBorrowerSsn} onChange={setCoBorrowerSsn} placeholder="XXX-XX-XXXX" data-id="element-331" />
                      <DateField label="Date of Birth" value={coBorrowerDob} onChange={setCoBorrowerDob} data-id="element-332" />
                    </FieldGrid>
                    <TextField label="Credit Score" value={coBorrowerCreditScore} onChange={setCoBorrowerCreditScore} data-id="element-333" />
                  </div>
                </SectionCard>

                <SectionCard title="Employment" data-id="element-334">
                  <div className="space-y-4" data-id="element-335">
                    <FieldGrid data-id="element-336">
                      <TextField label="Employer" value={coBorrowerEmployer} onChange={setCoBorrowerEmployer} data-id="element-337" />
                      <TextField label="Position / Title" value={coBorrowerPosition} onChange={setCoBorrowerPosition} data-id="element-338" />
                    </FieldGrid>
                    <FieldGrid data-id="element-339">
                      <TextField label="Years Employed" value={coBorrowerYears} onChange={setCoBorrowerYears} data-id="element-340" />
                      <CurrencyField label="Annual Income" value={coBorrowerIncome} onChange={setCoBorrowerIncome} data-id="element-341" />
                    </FieldGrid>
                    <RadioGroup label="Employment Type" value={coBorrowerEmploymentType} onChange={setCoBorrowerEmploymentType} options={[{
                  value: '1099',
                  label: '1099'
                }, {
                  value: 'W2',
                  label: 'W2'
                }, {
                  value: 'Self Employed',
                  label: 'Self Employed'
                }]} layout="horizontal" data-id="element-342" />
                  </div>
                </SectionCard>

                <SectionCard title="Previous Employment" data-id="element-343">
                  <div className="space-y-4" data-id="element-344">
                    <FieldGrid data-id="element-345">
                      <TextField label="Employer" value={coBorrowerPrevEmployer} onChange={setCoBorrowerPrevEmployer} data-id="element-346" />
                      <TextField label="Position / Title" value={coBorrowerPrevPosition} onChange={setCoBorrowerPrevPosition} data-id="element-347" />
                    </FieldGrid>
                    <FieldGrid data-id="element-348">
                      <TextField label="Years Employed" value={coBorrowerPrevYears} onChange={setCoBorrowerPrevYears} data-id="element-349" />
                      <CurrencyField label="Annual Income" value={coBorrowerPrevIncome} onChange={setCoBorrowerPrevIncome} data-id="element-350" />
                    </FieldGrid>
                  </div>
                </SectionCard>

                <SectionCard title="Additional Income" data-id="element-351">
                  <FieldGrid data-id="element-352">
                    <CurrencyField label="SSI" value={coBorrowerSsi} onChange={setCoBorrowerSsi} data-id="element-353" />
                    <CurrencyField label="Pension" value={coBorrowerPension} onChange={setCoBorrowerPension} data-id="element-354" />
                    <CurrencyField label="VA Disability" value={coBorrowerVaDisability} onChange={setCoBorrowerVaDisability} data-id="element-355" />
                    <CurrencyField label="Additional Income" value={coBorrowerAdditionalIncome} onChange={setCoBorrowerAdditionalIncome} data-id="element-356" />
                  </FieldGrid>
                  <div className="mt-4" data-id="element-357">
                    <TextField label="Next Pay Raise" value={coBorrowerNextPayRaise} onChange={setCoBorrowerNextPayRaise} data-id="element-358" />
                  </div>
                </SectionCard>
              </>}
          </div>;
      case 3:
        // Subject Property
        return <div className="space-y-6" data-id="element-359">
            <SectionCard title="Occupancy" data-id="element-360">
              <RadioGroup label="Property Use" value={occupancy} onChange={setOccupancy} options={[{
              value: 'Primary Residence',
              label: 'Primary Residence'
            }, {
              value: 'Second Home',
              label: 'Second Home'
            }, {
              value: 'Investment Property',
              label: 'Investment Property'
            }]} required data-id="element-361" />
            </SectionCard>

            <SectionCard title="Property Details" data-id="element-362">
              <div className="space-y-4" data-id="element-363">
                <RadioGroup label="Property Type" value={propertyType} onChange={setPropertyType} options={[{
                value: 'Condo',
                label: 'Condo'
              }, {
                value: 'Single Family Residence',
                label: 'Single Family Residence'
              }, {
                value: 'Townhouse',
                label: 'Townhouse'
              }, {
                value: 'Manufactured Home',
                label: 'Manufactured Home'
              }, {
                value: 'Mobile Home',
                label: 'Mobile Home'
              }, {
                value: '2-4 Unit',
                label: '2-4 Unit'
              }]} required data-id="element-364" />
                {propertyType === 'Condo' && <FieldGrid data-id="element-365">
                    <YesNoToggle label="FHA Approved Condo?" value={fhaApproved} onChange={setFhaApproved} data-id="element-366" />
                    <YesNoToggle label="VA Approved Condo?" value={vaApproved} onChange={setVaApproved} data-id="element-367" />
                  </FieldGrid>}
              </div>
            </SectionCard>

            <SectionCard title="Property Address" data-id="element-368">
              <div className="space-y-4" data-id="element-369">
                <CheckboxField label="Property not selected yet" checked={propertyNotSelected} onChange={setPropertyNotSelected} data-id="element-370" />
                {!propertyNotSelected ? <>
                    <TextField label="Subject Property Address" value={propertyAddress} onChange={setPropertyAddress} required data-id="element-371" />
                    <FieldGrid data-id="element-372">
                      <TextField label="City" value={propertyCity} onChange={setPropertyCity} required data-id="element-373" />
                      <SelectField label="State" value={propertyState} onChange={setPropertyState} options={US_STATES} required data-id="element-374" />
                    </FieldGrid>
                    <TextField label="Zip Code" value={propertyZip} onChange={setPropertyZip} required data-id="element-375" />
                  </> : <TextField label="Desired Zip Code" value={desiredZip} onChange={setDesiredZip} required data-id="element-376" />}
              </div>
            </SectionCard>
          </div>;
      case 4:
        // Purchase Details
        return <div className="space-y-6" data-id="element-377">
            <SectionCard title="Purchase Information" data-id="element-378">
              <div className="space-y-4" data-id="element-379">
                <CurrencyField label="Purchase Price" value={purchasePrice} onChange={setPurchasePrice} required data-id="element-380" />
                <FieldGrid data-id="element-381">
                  <PercentField label="Down Payment (%)" value={downPaymentPercent} onChange={setDownPaymentPercent} data-id="element-382" />
                  <CurrencyField label="Down Payment ($)" value={downPaymentDollar} onChange={setDownPaymentDollar} data-id="element-383" />
                </FieldGrid>
                <RadioGroup label="Loan Product" value={loanProduct} onChange={setLoanProduct} options={[{
                value: 'VA',
                label: 'VA'
              }, {
                value: 'FHA',
                label: 'FHA'
              }, {
                value: 'Conventional',
                label: 'Conventional'
              }]} layout="horizontal" required data-id="element-384" />
              </div>
            </SectionCard>

            <SectionCard title="Purchase Contract" data-id="element-385">
              <div className="space-y-4" data-id="element-386">
                <YesNoToggle label="Purchase Agreement Signed?" value={contractSigned} onChange={setContractSigned} data-id="element-387" />
                <DateField label="Estimated Closing Date" value={closingDate} onChange={setClosingDate} data-id="element-388" />
              </div>
            </SectionCard>

            <SectionCard title="Realtor Information" data-id="element-389">
              <div className="space-y-4" data-id="element-390">
                <YesNoToggle label="Has Realtor?" value={hasRealtor} onChange={setHasRealtor} data-id="element-391" />
                {hasRealtor && <FieldGrid data-id="element-392">
                    <TextField label="Realtor Name" value={realtorName} onChange={setRealtorName} data-id="element-393" />
                    <PhoneField label="Realtor Phone" value={realtorPhone} onChange={setRealtorPhone} data-id="element-394" />
                  </FieldGrid>}
              </div>
            </SectionCard>

            <SectionCard title="Earnest Money" data-id="element-395">
              <div className="space-y-4" data-id="element-396">
                <YesNoToggle label="Earnest Money Deposit Made?" value={earnestMoneyMade} onChange={setEarnestMoneyMade} data-id="element-397" />
                {earnestMoneyMade && <CurrencyField label="Earnest Money Amount" value={earnestMoneyAmount} onChange={setEarnestMoneyAmount} data-id="element-398" />}
              </div>
            </SectionCard>
          </div>;
      case 5:
        // Housing Expenses
        return <div className="space-y-6" data-id="element-399">
            <SectionCard title="Monthly Housing Expense Estimates" data-id="element-400">
              <div className="space-y-4" data-id="element-401">
                <YesNoToggle label="Escrowed" value={escrowed} onChange={setEscrowed} data-id="element-402" />
                <FieldGrid data-id="element-403">
                  <CurrencyField label="Property Taxes (Monthly)" value={propertyTaxes} onChange={setPropertyTaxes} data-id="element-404" />
                  <CurrencyField label="Homeowners Insurance (Monthly)" value={homeownersInsurance} onChange={setHomeownersInsurance} data-id="element-405" />
                  <CurrencyField label="PMI (Monthly)" value={pmi} onChange={setPmi} data-id="element-406" />
                  <CurrencyField label="Flood Insurance (Monthly)" value={floodInsurance} onChange={setFloodInsurance} data-id="element-407" />
                  <CurrencyField label="HOA (Monthly)" value={hoa} onChange={setHoa} data-id="element-408" />
                </FieldGrid>
              </div>
            </SectionCard>
          </div>;
      case 6:
        // Assets & Credit
        return <div className="space-y-6" data-id="element-409">
            <SectionCard title="Assets" data-id="element-410">
              <div className="space-y-4" data-id="element-411">
                <SelectField label="Asset Type" value={assetType} onChange={setAssetType} options={[{
                value: 'Checking',
                label: 'Checking'
              }, {
                value: 'Savings',
                label: 'Savings'
              }, {
                value: 'IRA',
                label: 'IRA'
              }, {
                value: '401k',
                label: '401(k)'
              }, {
                value: 'Stocks',
                label: 'Stocks'
              }]} data-id="element-412" />
                <CurrencyField label="Total Cash Value" value={assetValue} onChange={setAssetValue} data-id="element-413" />
              </div>
            </SectionCard>

            <SectionCard title="Credit Questions" data-id="element-414">
              <div className="space-y-4" data-id="element-415">
                <YesNoToggle label="Foreclosure in Last 7 Years?" value={foreclosure} onChange={setForeclosure} data-id="element-416" />
                <YesNoToggle label="Bankruptcy in Last 7 Years?" value={bankruptcy} onChange={setBankruptcy} data-id="element-417" />
                {bankruptcy && <RadioGroup label="Bankruptcy Chapter" value={bankruptcyChapter} onChange={setBankruptcyChapter} options={[{
                value: 'Chapter 7',
                label: 'Chapter 7'
              }, {
                value: 'Chapter 13',
                label: 'Chapter 13'
              }]} layout="horizontal" data-id="element-418" />}
                <YesNoToggle label="Liens Attached To Property?" value={liens} onChange={setLiens} data-id="element-419" />
                <YesNoToggle label="Late Mortgage Payments (Last 12 Months)?" value={lateMortgage} onChange={setLateMortgage} data-id="element-420" />
                <YesNoToggle label="Farm Activity On Property?" value={farmActivity} onChange={setFarmActivity} data-id="element-421" />
                <YesNoToggle label="Current Mortgage Exists?" value={currentMortgage} onChange={setCurrentMortgage} data-id="element-422" />
              </div>
            </SectionCard>

            <SectionCard title="Additional Risk Questions" data-id="element-423">
              <div className="space-y-4" data-id="element-424">
                <YesNoToggle label="Child Support Obligation?" value={childSupport} onChange={setChildSupport} data-id="element-425" />
                <YesNoToggle label="Loan Modification?" value={loanModification} onChange={setLoanModification} data-id="element-426" />
                <YesNoToggle label="Deferred Interest?" value={deferredInterest} onChange={setDeferredInterest} data-id="element-427" />
                <YesNoToggle label="Solar Panels?" value={solarPanels} onChange={setSolarPanels} data-id="element-428" />
                <YesNoToggle label="Mortgage Forbearance?" value={forbearance} onChange={setForbearance} data-id="element-429" />
                <YesNoToggle label="Reinstated?" value={reinstated} onChange={setReinstated} data-id="element-430" />
                <FieldGrid data-id="element-431">
                  <CurrencyField label="Amount Owed" value={amountOwed} onChange={setAmountOwed} data-id="element-432" />
                  <DateField label="Date Owed" value={dateOwed} onChange={setDateOwed} data-id="element-433" />
                </FieldGrid>
              </div>
            </SectionCard>

            <SectionCard title="Current Property" data-id="element-434">
              <YesNoToggle label="Selling Current Property?" value={sellingProperty} onChange={setSellingProperty} data-id="element-435" />
            </SectionCard>
          </div>;
      case 7:
        // Review
        return <div className="space-y-6" data-id="element-436">
            <SectionCard data-id="element-437">
              <div className="text-center py-8" data-id="element-438">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-439">
                  Review Your Application
                </h2>
                <p className="text-gray-600 mb-6" data-id="element-440">
                  Please review all the information you've provided. You can go
                  back to any step to make changes.
                </p>
                <div className="bg-blue-50 rounded-lg p-6 text-left" data-id="element-441">
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-442">
                    Application Summary
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700" data-id="element-443">
                    <p data-id="element-444">
                      <strong data-id="element-445">Borrower:</strong>{' '}
                      {borrowerName || 'Not provided'}
                    </p>
                    <p data-id="element-446">
                      <strong data-id="element-447">Email:</strong> {email || 'Not provided'}
                    </p>
                    <p data-id="element-448">
                      <strong data-id="element-449">Property Type:</strong>{' '}
                      {propertyType || 'Not provided'}
                    </p>
                    <p data-id="element-450">
                      <strong data-id="element-451">Purchase Price:</strong>{' '}
                      {purchasePrice ? `$${parseFloat(purchasePrice).toLocaleString()}` : 'Not provided'}
                    </p>
                    <p data-id="element-452">
                      <strong data-id="element-453">Loan Product:</strong>{' '}
                      {loanProduct || 'Not provided'}
                    </p>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>;
      default:
        return null;
    }
  };
  return <WizardShell steps={STEPS} completionPercentage={completionPercentage} headerContent={<CrmHeader data-id="element-455" />} currentStep={currentStep} onStepChange={setCurrentStep} onBack={handleBack} onNext={handleNext} onSubmit={handleSubmit} canGoBack={currentStep > 0} canGoNext={true} isLastStep={currentStep === STEPS.length - 1} data-id="element-454">
      <div className="space-y-6 pb-24" data-id="element-456">
        {STEPS.map((step, index) => <div key={step.id} id={step.id} className="scroll-mt-24" data-id="element-457">
            <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-2" data-id="element-458">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900" data-id="element-459">
                {step.label}
              </h2>
              <div className="hidden lg:block text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full" data-id="element-460">
                Section {index + 1} of {STEPS.length}
              </div>
            </div>
            {getStepContent(index)}
          </div>)}
      </div>
    </WizardShell>;
}
export function PurchaseApplication() {
  return <ApplicationProvider data-id="element-461">
      <PurchaseApplicationContent data-id="element-462" />
    </ApplicationProvider>;
}